import { Table, Badge, Group, Text, Card, Tooltip } from '@mantine/core'
import { formatDateTime, secondsToHHMM } from '@/utils/datetime'
import dayjs from 'dayjs'
import { DetailedDriveStatistics, PackageSubmissionDetail } from '@/api/main/types.gen'
import { getRouteOptions } from '@/api/main'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

interface DriveDetailsTableProps {
  driveStatistics: DetailedDriveStatistics
  packageSubmissionDetail?: PackageSubmissionDetail
}

const Th = ({ children, tooltip }: { children: React.ReactNode; tooltip: string }) => (
  <Table.Th w={200}>
    <Tooltip label={tooltip} multiline w={300} withArrow>
      <Text span style={{ cursor: 'help', borderBottom: '1px dotted var(--mantine-color-dimmed)' }}>
        {children}
      </Text>
    </Tooltip>
  </Table.Th>
)

const DriveDetailsTable = ({ driveStatistics, packageSubmissionDetail }: DriveDetailsTableProps) => {
  const { t } = useTranslation(['driveDetailsTooltips', 'driveDetails', 'common'])
  const { data: route } = useQuery(
    getRouteOptions({
      path: {
        route_id: driveStatistics.route_id,
        route_version: driveStatistics.route_version,
      },
    })
  )

  const formatDate = (dateString?: string) => {
    if (!dateString) return t('notAvailable', { ns: 'common' })
    return dayjs(dateString).format('MMM DD, YYYY HH:mm:ss')
  }

  const formatDriveType = (types: string[]) => {
    return types.map(type => type.charAt(0).toUpperCase() + type.slice(1)).join(', ')
  }

  const getStateColor = (state: string) => {
    switch (state) {
      case 'finished':
        return 'green'
      case 'in_progress':
        return 'blue'
      case 'pending':
        return 'yellow'
      case 'cancelled':
        return 'red'
      case 'cancelled_in_progress':
        return 'red'
      default:
        return 'gray'
    }
  }

  const getBillingTypeColor = (type: string) => {
    switch (type) {
      case 'billable':
        return 'green'
      case 'non_billable':
        return 'gray'
      case 'development':
        return 'blue'
      case 'disrespect_all':
        return 'red'
      default:
        return 'gray'
    }
  }

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Table variant="vertical" layout="fixed" withTableBorder>
        <Table.Tbody>
          <Table.Tr>
            <Th tooltip={t('driveId')}>{t('driveId', { ns: 'driveDetails' })}</Th>
            <Table.Td>{driveStatistics.drive_id}</Table.Td>
          </Table.Tr>

          <Table.Tr>
            <Th tooltip={t('vehicleId')}>{t('vehicleId', { ns: 'driveDetails' })}</Th>
            <Table.Td>{driveStatistics.vehicle_id}</Table.Td>
          </Table.Tr>

          <Table.Tr>
            <Th tooltip={t('drivers')}>{t('drivers', { ns: 'driveDetails' })}</Th>
            <Table.Td>
              <Group gap="xs">
                {driveStatistics.drivers.map(driver => (
                  <Badge key={driver.id} variant="light" color="blue">
                    {driver.name}
                  </Badge>
                ))}
              </Group>
            </Table.Td>
          </Table.Tr>

          <Table.Tr>
            <Th tooltip={t('route')}>{t('route', { ns: 'driveDetails' })}</Th>
            <Table.Td>
              {route?.name ? `${route.name} | ` : ''}
              <Link
                to="/route/$routeId/$version"
                params={{
                  routeId: driveStatistics.route_id.toString(),
                  version: driveStatistics.route_version.toString(),
                }}
                style={{
                  color: 'var(--mantine-color-blue-6)',
                  textDecoration: 'underline',
                }}
              >
                {driveStatistics.route_id} (v{driveStatistics.route_version})
              </Link>
            </Table.Td>
          </Table.Tr>

          <Table.Tr>
            <Th tooltip={t('planningArea')}>{t('planningArea', { ns: 'driveDetails' })}</Th>
            <Table.Td>{driveStatistics.planning_area_id}</Table.Td>
          </Table.Tr>

          <Table.Tr>
            <Th tooltip={t('district')}>{t('district', { ns: 'driveDetails' })}</Th>
            <Table.Td>
              {driveStatistics.district_code} - {driveStatistics.subdistrict_code}
            </Table.Td>
          </Table.Tr>

          <Table.Tr>
            <Th tooltip={t('state')}>{t('state', { ns: 'driveDetails' })}</Th>
            <Table.Td>
              <Badge color={getStateColor(driveStatistics.state)} variant="light">
                {driveStatistics.state.replace('_', ' ').toUpperCase()}
              </Badge>
            </Table.Td>
          </Table.Tr>

          <Table.Tr>
            <Th tooltip={t('driveType')}>{t('driveType', { ns: 'driveDetails' })}</Th>
            <Table.Td>{formatDriveType(driveStatistics.drive_type)}</Table.Td>
          </Table.Tr>

          <Table.Tr>
            <Th tooltip={t('billingType')}>{t('billingType', { ns: 'driveDetails' })}</Th>
            <Table.Td>
              <Badge color={getBillingTypeColor(driveStatistics.billing_type)} variant="light">
                {driveStatistics.billing_type.replace('_', ' ').toUpperCase()}
              </Badge>
            </Table.Td>
          </Table.Tr>

          <Table.Tr>
            <Th tooltip={t('startedAt')}>{t('startedAt', { ns: 'driveDetails' })}</Th>
            <Table.Td>{formatDate(driveStatistics.drive_started_at)}</Table.Td>
          </Table.Tr>

          <Table.Tr>
            <Th tooltip={t('finishedAt')}>{t('finishedAt', { ns: 'driveDetails' })}</Th>
            <Table.Td>{formatDate(driveStatistics.drive_finished_at)}</Table.Td>
          </Table.Tr>

          <Table.Tr>
            <Th tooltip={t('duration')}>{t('duration', { ns: 'driveDetails' })}</Th>
            <Table.Td>{secondsToHHMM(driveStatistics.duration_seconds)}</Table.Td>
          </Table.Tr>

          <Table.Tr>
            <Th tooltip={t('routePlanItems')}>{t('routePlanItems', { ns: 'driveDetails' })}</Th>
            <Table.Td>{driveStatistics.route_plan_item_count}</Table.Td>
          </Table.Tr>

          <Table.Tr>
            <Th tooltip={t('maxDrivenIndex')}>{t('maxDrivenIndex', { ns: 'driveDetails' })}</Th>
            <Table.Td>{driveStatistics.max_driven_route_index || t('notAvailable', { ns: 'common' })}</Table.Td>
          </Table.Tr>

          <Table.Tr>
            <Th tooltip={t('unexpectedEvents')}>{t('unexpectedEvents', { ns: 'driveDetails' })}</Th>
            <Table.Td>{driveStatistics.unexpected_event_count}</Table.Td>
          </Table.Tr>

          <Table.Tr>
            <Th tooltip={t('skippedSegments')}>{t('skippedSegments', { ns: 'driveDetails' })}</Th>
            <Table.Td>{driveStatistics.skipped_segment_count}</Table.Td>
          </Table.Tr>

          <Table.Tr>
            <Th tooltip={t('missedSegments')}>{t('missedSegments', { ns: 'driveDetails' })}</Th>
            <Table.Td>{driveStatistics.missed_segment_count}</Table.Td>
          </Table.Tr>

          <Table.Tr>
            <Th tooltip={t('detectedVehicles')}>{t('detectedVehicles', { ns: 'driveDetails' })}</Th>
            <Table.Td>
              <Table>
                <Table.Tbody>
                  <Table.Tr>
                    <Table.Td>{t('total', { ns: 'driveDetails' })}</Table.Td>
                    <Table.Td>{packageSubmissionDetail?.vehicle_detections}</Table.Td>
                  </Table.Tr>
                  <Table.Tr>
                    <Table.Td>{t('disrespects', { ns: 'driveDetails' })}</Table.Td>
                    <Table.Td>{packageSubmissionDetail?.no_respect_vehicle_detections}</Table.Td>
                  </Table.Tr>
                  <Table.Tr>
                    <Table.Td>{t('submittedPackages', { ns: 'driveDetails' })}</Table.Td>
                    <Table.Td>{packageSubmissionDetail?.submitted_no_respect_evidence_packages}</Table.Td>
                  </Table.Tr>
                </Table.Tbody>
              </Table>
            </Table.Td>
          </Table.Tr>

          <Table.Tr>
            <Th tooltip={t('detectedSignPosts')}>{t('detectedSignPosts', { ns: 'driveDetails' })}</Th>
            <Table.Td>
              <Table>
                <Table.Tbody>
                  <Table.Tr>
                    <Table.Td>{t('total', { ns: 'driveDetails' })}</Table.Td>
                    <Table.Td>{packageSubmissionDetail?.sign_post_detections}</Table.Td>
                  </Table.Tr>
                  <Table.Tr>
                    <Table.Td>{t('submittedPackages', { ns: 'driveDetails' })}</Table.Td>
                    <Table.Td>{packageSubmissionDetail?.submitted_sign_posts_evidence_packages}</Table.Td>
                  </Table.Tr>
                </Table.Tbody>
              </Table>
            </Table.Td>
          </Table.Tr>

          <Table.Tr>
            <Th tooltip={t('plannedRouteSegments')}>{t('plannedRouteSegments', { ns: 'driveDetails' })}</Th>
            <Table.Td>{driveStatistics.planned_route_segment_count}</Table.Td>
          </Table.Tr>

          <Table.Tr>
            <Th tooltip={t('requiredRouteSegments')}>{t('requiredRouteSegments', { ns: 'driveDetails' })}</Th>
            <Table.Td>{driveStatistics.required_route_segment_count}</Table.Td>
          </Table.Tr>

          <Table.Tr>
            <Th tooltip={t('drivenRouteSegments')}>{t('drivenRouteSegments', { ns: 'driveDetails' })}</Th>
            <Table.Td>{driveStatistics.driven_route_segment_count}</Table.Td>
          </Table.Tr>

          <Table.Tr>
            <Th tooltip={t('drivenPlannedSegments')}>{t('drivenPlannedSegments', { ns: 'driveDetails' })}</Th>
            <Table.Td>{driveStatistics.driven_planned_route_segment_count}</Table.Td>
          </Table.Tr>

          <Table.Tr>
            <Th tooltip={t('drivenOverheadSegments')}>{t('drivenOverheadSegments', { ns: 'driveDetails' })}</Th>
            <Table.Td>{driveStatistics.driven_overhead_segment_count}</Table.Td>
          </Table.Tr>

          {driveStatistics.cancellation_reason && (
            <Table.Tr>
              <Th tooltip={t('cancellationReason')}>{t('cancellationReason', { ns: 'driveDetails' })}</Th>
              <Table.Td>
                <Text c="red">{driveStatistics.cancellation_reason}</Text>
              </Table.Td>
            </Table.Tr>
          )}
          <Table.Tr>
            <Th tooltip={t('scheduledInterval')}>{t('scheduledInterval', { ns: 'driveDetails' })}</Th>
            <Table.Td>
              {t('from', { ns: 'driveDetails' })}: {formatDateTime(driveStatistics.start_after)}
              <br />
              {t('to', { ns: 'driveDetails' })}: {formatDateTime(driveStatistics.finish_before)}
            </Table.Td>
          </Table.Tr>
        </Table.Tbody>
      </Table>
    </Card>
  )
}

export default DriveDetailsTable
